import type { MenuItem } from "@/types";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddIcon from "@mui/icons-material/Add";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ReplayIcon from "@mui/icons-material/Replay";
import StarIcon from "@mui/icons-material/Star";
import {
  Box,
  Chip,
  Fab,
  IconButton,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";

import ItemPrice from "@/components/menu/ItemPrice";
import { PAGE_PADDING, SHAPE, gutterFor } from "@/constants/layout";
import useImageVariant from "@/hooks/useImageVariant";
import { useAppSelector } from "@/store";
import { selectCatalogItemsMap } from "@/store/selectors/catalog";
import { buildReorderableItems, type ReorderableItem } from "@/lib/reorder";

/**
 * "Order Again" — the original's `nq` (`OrderAgain`) in
 * `recovery-source/live-chunks/mod-9802.js`, with its card `nD`.
 *
 * It is the SECOND of the two rails a signed-in customer sees above the
 * catalog: `rw` (Recent Orders) lists the orders, this one lists the ITEMS
 * inside them, ranked by how often the customer has ordered each one. Both are
 * gated together on `!isStaff && isLoggedIn` by their parent, and both report
 * upward so the synthetic category tabs can appear.
 *
 * Its whole data source is `state.history.recentOrders` — no request of its
 * own — intersected with the live catalog, so an item that has since been
 * delisted simply drops out.
 */

/* -------------------------------- styling --------------------------------- */

/** `nQ` — same gutter ladder as every other full-width section. */
const Section = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isModernDesign",
})<{ isModernDesign?: boolean }>(({ theme, isModernDesign }) => {
  const gutter = gutterFor(isModernDesign);
  return {
    borderRadius: SHAPE.borderRadius,
    marginTop: 0,
    marginRight: PAGE_PADDING,
    marginBottom: theme.spacing(4),
    marginLeft: PAGE_PADDING,
    [theme.breakpoints.up("lg")]: {
      marginRight: gutter.lg,
      marginLeft: gutter.lg,
    },
    [theme.breakpoints.up("xl")]: {
      marginRight: gutter.xl,
      marginLeft: gutter.xl,
    },
    [theme.breakpoints.up("xxl")]: {
      marginRight: gutter.xxl,
      marginLeft: gutter.xxl,
    },
    [theme.breakpoints.up("xxxl")]: {
      marginRight: gutter.xxxl,
      marginLeft: gutter.xxxl,
    },
  };
});

/** `nX` */
const Header = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

/** `nJ` — note it is NOT uppercased, unlike the Recent Orders heading. */
const Heading = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
}));

/** `n$` — identical to the Recent Orders rail's `rk`. */
const ScrollButton = styled(IconButton)(({ theme }) => ({
  border: `1px solid ${theme.palette.text.primary}`,
  borderRadius: "50%",
  transition: theme.transitions.create(["all"], { duration: 300 }),
  "&:hover:not(:disabled)": {
    backgroundColor: theme.palette.action.hover,
    boxShadow: theme.shadows[2],
    transform: "scale(1.05)",
  },
  "&:disabled": {
    opacity: 0.4,
    cursor: "not-allowed",
    color: theme.palette.text.disabled,
    borderColor: theme.palette.action.disabledBackground,
  },
}));

/** `n0` — the scroll port. */
const Scroller = styled(Stack)({
  display: "flex",
  overflow: "hidden",
  overflowX: "auto",
  scrollbarWidth: "none",
  paddingBottom: 8,
  marginTop: 5,
  "&::-webkit-scrollbar": { display: "none" },
});

/* --------------------------------- the card -------------------------------- */

/**
 * `nz`. Its `isSoldOut` branch is styled but never reached: `nD` does not pass
 * the prop even though it carries `sold_out` on the item. Faithful — the
 * grey-out is dead code in the original too.
 */
const Card = styled(Box)(({ theme }) => ({
  position: "relative",
  height: "auto",
  minHeight: 280,
  width: "100%",
  maxWidth: 320,
  minWidth: 200,
  boxShadow: "rgba(0, 0, 0, 0.08) 0px 2px 8px",
  borderRadius: theme.spacing(2),
  overflow: "hidden",
  cursor: "pointer",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.up("sm")]: { minHeight: 320, maxWidth: 280 },
  [theme.breakpoints.up("md")]: { minHeight: 340, maxWidth: 300 },
  [theme.breakpoints.up("lg")]: { minHeight: 360, maxWidth: 320 },
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: theme.shadows[12],
    borderColor: theme.palette.primary.main,
  },
  "&:active": { transform: "translateY(-3px) scale(0.98)" },
}));

/** `nA` */
const ImageFrame = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: 160,
  background: `linear-gradient(135deg, ${theme.palette.grey[100]} 0%, ${theme.palette.grey[200]} 100%)`,
  overflow: "hidden",
  flexShrink: 0,
  [theme.breakpoints.up("sm")]: { height: 180 },
  [theme.breakpoints.up("md")]: { height: 200 },
  [theme.breakpoints.up("lg")]: { height: 220 },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.02)",
    zIndex: 1,
  },
}));

/** `nN` */
const CardImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center",
  display: "block",
  transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": { transform: "scale(1.08)" },
});

/** `nF` — the bottom scrim, so the "Reorder" chip stays legible on a pale photo. */
const Scrim = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.15) 100%)",
  pointerEvents: "none",
  zIndex: 2,
});

/** `nW` */
const PriceSlot = styled(Box)({
  position: "absolute",
  top: 12,
  left: 12,
  zIndex: 3,
});

/** `nL` — the quick-add Fab. */
const AddFab = styled(Fab)(({ theme }) => ({
  position: "absolute",
  top: 8,
  right: 8,
  zIndex: 3,
  width: 32,
  height: 32,
  minHeight: 32,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  boxShadow: theme.shadows[4],
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  [theme.breakpoints.up("sm")]: {
    width: 36,
    height: 36,
    minHeight: 36,
    top: 12,
    right: 12,
  },
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
    transform: "scale(1.1)",
    boxShadow: theme.shadows[8],
  },
  "&:active": { transform: "scale(0.95)" },
}));

/** `nY` — the pulsing heart, shown once an item has been ordered MORE than 3 times. */
const FavoriteBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 8,
  right: 48,
  zIndex: 3,
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  padding: "3px 6px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  animation: "pulse 2s infinite",
  [theme.breakpoints.up("sm")]: { top: 12, right: 60, padding: "4px 8px" },
  "@keyframes pulse": {
    "0%": { transform: "scale(1)" },
    "50%": { transform: "scale(1.1)" },
    "100%": { transform: "scale(1)" },
  },
}));

/** `nB` — "Top Pick", shown from the SECOND order onward. */
const TopPickBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 40,
  left: 8,
  zIndex: 3,
  backgroundColor: theme.palette.warning.main,
  color: theme.palette.warning.contrastText,
  padding: "2px 4px",
  borderRadius: "6px",
  fontSize: "0.6rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  display: "flex",
  alignItems: "center",
  [theme.breakpoints.up("sm")]: {
    bottom: 50,
    left: 12,
    padding: "2px 6px",
    borderRadius: "8px",
    fontSize: "0.65rem",
  },
}));

/** `nG` — the "Reorder" chip. */
const ReorderBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 8,
  left: 8,
  zIndex: 3,
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.secondary.contrastText,
  padding: "3px 6px",
  borderRadius: "10px",
  fontSize: "0.65rem",
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.25),
  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  [theme.breakpoints.up("sm")]: {
    bottom: 12,
    left: 12,
    padding: "4px 8px",
    borderRadius: "12px",
    fontSize: "0.7rem",
    gap: theme.spacing(0.5),
  },
}));

/** `nH` — the basket count, which REPLACES the add Fab rather than sitting beside it. */
const QuantityBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 8,
  right: 8,
  zIndex: 3,
  width: 32,
  height: 32,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 700,
  fontSize: "0.9rem",
  boxShadow: theme.shadows[4],
  [theme.breakpoints.up("sm")]: {
    top: 12,
    right: 12,
    width: 36,
    height: 36,
    fontSize: "1rem",
  },
}));

/** `nU` */
const Body = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 1.5),
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  justifyContent: "space-between",
  [theme.breakpoints.up("sm")]: { padding: theme.spacing(2, 2), gap: theme.spacing(1) },
  [theme.breakpoints.up("md")]: { padding: theme.spacing(2.5, 2) },
}));

/** `nV` — two-line clamp. */
const Name = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.9rem",
  color: theme.palette.text.primary,
  lineHeight: 1.3,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  marginBottom: theme.spacing(0.25),
  [theme.breakpoints.up("sm")]: { fontSize: "1rem", marginBottom: theme.spacing(0.5) },
}));

/** `nK` */
const Footnote = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  [theme.breakpoints.up("sm")]: { fontSize: "0.85rem" },
}));

/* --------------------------------- the card -------------------------------- */

interface ReorderCardProps {
  item: ReorderableItem;
  quantity?: number;
  symbol?: string;
  code?: string;
  onClick: () => void;
}

/** `nD` */
function ReorderCard({ item, quantity, symbol, code, onClick }: ReorderCardProps) {
  const { imageSrc, onImageError } = useImageVariant(item.image_url, "512x512");
  return (
    <Card onClick={onClick}>
      <ImageFrame>
        {imageSrc && (
          <CardImage
            src={imageSrc}
            alt={item.name}
            loading="lazy"
            onError={onImageError}
          />
        )}
        <Scrim />
        <PriceSlot>
          <ItemPrice
            item={{
              _id: item._id,
              category_id: "",
              name: item.name,
              price: item.price,
              discounted_price: item.discounted_price,
              discount_amount: item.discount_amount,
              is_discount_percentage: item.is_discount_percentage,
              show_starting_from: item.show_starting_from,
            }}
            symbol={symbol}
            code={code}
            color="secondary"
          />
        </PriceSlot>
        {item.isFavorite && (
          <FavoriteBadge>
            <FavoriteIcon fontSize="small" />
          </FavoriteBadge>
        )}
        {quantity ? (
          <QuantityBadge>{quantity}</QuantityBadge>
        ) : (
          <AddFab
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onClick();
            }}
          >
            <AddIcon fontSize="small" />
          </AddFab>
        )}
        <Stack flexDirection="row" spacing={1}>
          <ReorderBadge>
            <ReplayIcon fontSize="inherit" />
            Reorder
          </ReorderBadge>
          {item.orderFrequency > 1 && (
            <TopPickBadge>
              <StarIcon fontSize="inherit" />
              Top Pick
            </TopPickBadge>
          )}
        </Stack>
      </ImageFrame>
      <Body>
        <Name>{item.name}</Name>
        {item.modifiers.length > 0 && (
          <Box>
            {item.modifiers.map((modifier, index) => (
              <Typography
                key={`${modifier}-${index}`}
                variant="body2"
                color="text.secondary"
              >
                {modifier}
              </Typography>
            ))}
          </Box>
        )}
        <Footnote>
          {item.isFavorite && "❤️ Your favorite • "}
          Ordered {item.orderFrequency} time{item.orderFrequency > 1 ? "s" : ""}
        </Footnote>
      </Body>
    </Card>
  );
}

/* --------------------------------- the rail -------------------------------- */

interface OrderAgainProps {
  /**
   * The scroll target its synthetic category tab jumps to. The original keeps a
   * ref per section and scrolls by `offsetTop`; this tree scrolls the same
   * sections by id, so the id is the section's handle.
   */
  id?: string;
  /** Basket quantity per item id, so a card can show its count. */
  quantities: Record<string, number | undefined>;
  symbol?: string;
  code?: string;
  isModernDesign?: boolean;
  onItemClick: (item: MenuItem) => void;
}

/** `nq` */
const OrderAgain = forwardRef<HTMLDivElement, OrderAgainProps>(
  function OrderAgain(
    { id, quantities, symbol, code, isModernDesign, onItemClick },
    ref,
  ) {
    const customer = useAppSelector((state) => state.auth.customer);
    const recentOrders = useAppSelector((state) => state.history.recentOrders);
    const catalogItemsMap = useAppSelector(selectCatalogItemsMap);

    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const items = useMemo(
      () => buildReorderableItems(recentOrders, catalogItemsMap),
      [recentOrders, catalogItemsMap],
    );

    useEffect(() => {
      const element = scrollRef.current;
      if (!element) return undefined;
      const update = () => {
        setCanScrollLeft(element.scrollLeft > 0);
        setCanScrollRight(
          element.scrollLeft + element.clientWidth < element.scrollWidth - 1,
        );
      };
      update();
      element.addEventListener("scroll", update);
      window.addEventListener("resize", update);
      return () => {
        element.removeEventListener("scroll", update);
        window.removeEventListener("resize", update);
      };
    }, [items.length]);

    const scroll = (direction: "left" | "right") => {
      scrollRef.current?.scrollBy({
        left: direction === "left" ? -300 : 300,
        behavior: "smooth",
      });
    };

    /** `_` — identical to the Recent Orders rail's, down to the raw 480px query. */
    const renderScrollButtons = (
      direction: "row" | "column",
      { hideBelow, hideAbove }: { hideBelow?: number; hideAbove?: number },
    ) => {
      const sx: Record<string, unknown> = {
        display: "flex",
        alignSelf: "center",
        ml: 1,
      };
      if (hideBelow !== undefined) {
        sx[`@media (max-width: ${hideBelow - 1}px)`] = { display: "none" };
      }
      if (hideAbove !== undefined) {
        sx[`@media (min-width: ${hideAbove}px)`] = { display: "none" };
      }
      return (
        <Stack
          direction={direction}
          spacing={1}
          sx={sx}
          alignItems="center"
          justifyContent="center"
        >
          <ScrollButton
            size="small"
            onClick={() => scroll("left")}
            disabled={!canScrollLeft}
            aria-label="scroll left"
            sx={{ borderRadius: "50%", minWidth: "auto", width: 36, height: 36 }}
          >
            <ArrowBackIcon fontSize="small" />
          </ScrollButton>
          <ScrollButton
            size="small"
            onClick={() => scroll("right")}
            disabled={!canScrollRight}
            aria-label="scroll right"
            sx={{ borderRadius: "50%", minWidth: "auto", width: 36, height: 36 }}
          >
            <ArrowForwardIcon fontSize="small" />
          </ScrollButton>
        </Stack>
      );
    };

    // No loading branch and no empty state: the original renders nothing until
    // it has something to reorder.
    if (!items.length) return null;

    return (
      <Section ref={ref} id={id} isModernDesign={isModernDesign}>
        <Header>
          <Heading variant="h5">Order Again</Heading>
          <Typography variant="body2" color="text.secondary">
            {customer?.display_name
              ? `Welcome back, ${customer.display_name}! Here are your favorites`
              : "Quick reorder from your recent orders"}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip
              icon={<FavoriteIcon color="secondary" />}
              label="Your Favorites"
              size="small"
              sx={{
                backgroundColor: "primary.light",
                color: "primary.contrastText",
                fontSize: "0.7rem",
                height: "24px",
              }}
            />
            <Chip
              icon={<StarIcon color="info" />}
              label="Top Picks"
              size="small"
              sx={{
                backgroundColor: "secondary.light",
                color: "secondary.contrastText",
                fontSize: "0.7rem",
                height: "24px",
              }}
            />
          </Stack>
        </Header>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Scroller ref={scrollRef} direction="row" spacing={3} sx={{ flex: 1 }}>
            <Stack direction="row" spacing={3} pt={2}>
              {items.map((item) => (
                <ReorderCard
                  key={item._id}
                  item={item}
                  quantity={quantities[item.item_id]}
                  symbol={symbol}
                  code={code}
                  onClick={() => {
                    const catalogItem = catalogItemsMap[item.item_id];
                    if (catalogItem) onItemClick(catalogItem);
                  }}
                />
              ))}
            </Stack>
          </Scroller>
          {renderScrollButtons("column", { hideBelow: 480 })}
        </Stack>
        {renderScrollButtons("row", { hideAbove: 480 })}
      </Section>
    );
  },
);

export default OrderAgain;
